import { z } from 'zod';
import { apiGet, apiSend } from './api';
import type { Okno } from './permissions-api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  REGUŁY LIMITU WPROST — po identyfikatorze reguły, nie przez wpis dostępu.
 *
 *  ⚠️⚠️ POWSTAŁO 07.09.2026 DLA LIMITÓW IMIENNYCH NA KARCIE PRACOWNIKA. Ekran Uprawnień zapisuje
 *  limit przez wpis dostępu (`setPermissionLimit(idWpisu)`) i to jest tam właściwe: limit wisi
 *  przy uprawnieniu, które ogranicza. Ale limit IMIENNY człowieka, który dostęp ma przez GRUPĘ,
 *  nie ma własnego wpisu dostępu — a jednak obowiązuje. Zmierzone na bazie klienta: taka reguła
 *  zablokowała pracownika na 11 towarów grupy na rok, będąc niewidoczną w miejscu edycji.
 *
 *  ⚠️ DLATEGO TA DROGA ADRESUJE REGUŁĘ, A NIE UPRAWNIENIE. Trasy istniały od dawna
 *  (`POST/PATCH/DELETE /rationing`), tylko klient ich nie wołał, odkąd moduł Ograniczeń wszedł
 *  do Uprawnień — nie dokładamy więc drugiego zapisu do SPS, a jedynie brakujące wywołanie.
 *
 *  ⚠️ EDYCJA ZMIENIA WYŁĄCZNIE WIELKOŚĆ I OKNO. Cel i zakres są kluczem naturalnym reguły
 *  w SPS, więc ich podmiana to usunięcie jednej reguły i utworzenie drugiej — instancja tego
 *  pilnuje (`UpdateRationingSchema` przyjmuje same liczby).
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

const OdpowiedzOperacji = z.object({ opId: z.string() });

/** Nowa reguła imienna: człowiek + JEDEN zakres (towar albo grupa główna). */
export const utworzLimitImienny = (
  groupId: string,
  employeeId: string,
  zakres: { itemId?: string; itemGroupId?: string },
  okno: Okno,
) =>
  apiSend(
    'POST',
    '/rationing',
    {
      groupId,
      employeeId,
      ...(zakres.itemId === undefined ? {} : { itemId: zakres.itemId }),
      ...(zakres.itemGroupId === undefined ? {} : { itemGroupId: zakres.itemGroupId }),
      ...okno,
    },
    OdpowiedzOperacji,
  );

export const zmienLimitReguly = (id: string, okno: Okno) =>
  apiSend('PATCH', `/rationing/${encodeURIComponent(id)}`, { ...okno }, OdpowiedzOperacji);

export const usunLimitReguly = (id: string, groupId: string) =>
  apiSend(
    'DELETE',
    `/rationing/${encodeURIComponent(id)}?groupId=${encodeURIComponent(groupId)}`,
    undefined,
    OdpowiedzOperacji,
  );

/**
 * ZAKRESY DO WYBORU przy nowej regule.
 *
 * ⚠️⚠️ PODGRUPY PRZYCHODZĄ Z FLAGĄ `isSubGroup` I MUSZĄ BYĆ ODSIANE albo wyraźnie opisane:
 * `Rationing.ITEMTYPE` zna wyłącznie towar i grupę GŁÓWNĄ, więc reguła na podgrupie nie istnieje
 * jako zapis. Instancja odrzuci taki wybór — formularz nie ma go proponować bez uprzedzenia.
 */
const OpcjeSchema = z.object({
  itemGroups: z.array(
    z.object({ id: z.string(), code: z.string(), name: z.string(), items: z.number(), isSubGroup: z.boolean() }),
  ),
  items: z.array(z.object({ id: z.string(), itemNumber: z.string(), name: z.string(), unit: z.string().nullable() })),
});

export type OpcjeZakresu = z.infer<typeof OpcjeSchema>;

export const opcjeZakresuLimitu = (groupId: string, szukaj: string) =>
  apiGet(
    `/rationing/options?groupId=${encodeURIComponent(groupId)}` +
      `${szukaj === '' ? '' : `&search=${encodeURIComponent(szukaj)}`}`,
    OpcjeSchema,
  );
